import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const LoginPage = () => {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginType, setLoginType] = useState('domain');
  const [fetchingLoginType, setFetchingLoginType] = useState(true);
  const [adaptiveAuthType, setAdaptiveAuthType] = useState('password');
  const [checkingTokens, setCheckingTokens] = useState(false);

  React.useEffect(() => {
    const fetchLoginType = async () => {
      try {
        const response = await axios.get(`${API}/auth/login-type`);
        setLoginType(response.data.login_type);
      } catch (error) {
        console.error('Failed to fetch login type:', error);
        setLoginType('domain');
      } finally {
        setFetchingLoginType(false);
      }
    };
    fetchLoginType();
  }, []);

  const handleUsernameBlur = async () => {
    if (loginType !== 'adaptive' || !username.trim()) {
      return;
    }

    setCheckingTokens(true);
    try {
      const response = await axios.post(`${API}/auth/check-user-tokens`, {
        username: username.trim()
      });
      
      if (response.data.has_tokens) {
        setAdaptiveAuthType('otp');
      } else {
        setAdaptiveAuthType('password');
      }
    } catch (error) {
      console.error('Failed to check user tokens:', error);
      setAdaptiveAuthType('password');
    } finally {
      setCheckingTokens(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const loginData = {
        username,
        password,
      };

      if (loginType === 'mixed') {
        loginData.otp = otp;
      }

      const response = await axios.post(`${API}/auth/login`, loginData);

      if (response.data.success) {
        // Store session ID and username in localStorage for Mi-Token API calls
        if (response.data.session) {
          localStorage.setItem('session', response.data.session);
        }
        localStorage.setItem('username', username);
        
        login(username);
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail || 'Login failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getPasswordLabel = () => {
    if (loginType === 'token') {
      return 'One-Time Password (OTP)';
    }
    if (loginType === 'adaptive') {
      return adaptiveAuthType === 'otp' ? 'One-Time Password (OTP)' : t('login.password');
    }
    if (loginType === 'mixed') {
      return t('login.password');
    }
    return t('login.password');
  };

  const getPasswordPlaceholder = () => {
    if (loginType === 'token') {
      return 'Enter your OTP';
    }
    if (loginType === 'adaptive') {
      return adaptiveAuthType === 'otp' ? 'Enter your OTP' : 'Enter your password';
    }
    if (loginType === 'mixed') {
      return 'Enter your password';
    }
    return 'Enter your password';
  };

  const getInstructions = () => {
    if (loginType === 'token') {
      return 'Please enter your username and the One-Time Password (OTP) from your Mi-Token device to access your account.';
    }
    if (loginType === 'adaptive') {
      if (adaptiveAuthType === 'otp') {
        return 'You have tokens assigned. Please enter your username and the One-Time Password (OTP) from your Mi-Token device.';
      }
      return 'Please enter your username and password. The system will automatically determine the appropriate authentication method.';
    }
    if (loginType === 'mixed') {
      return 'Please enter your username, password, and One-Time Password (OTP) from your Mi-Token device. All three fields are required.';
    }
    return t('login.instructions');
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Hero Image */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-slate-900 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1636142466028-8c6c7cd616b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmUlMjBnbGFzcyUyMGJ1aWxkaW5nfGVufDB8fHx8MTc2NTIzNTYzM3ww&ixlib=rb-4.1.0&q=85"
          alt="Corporate Building"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" />
        
        {/* Overlay Content */}
        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <div className="mb-8 space-y-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_id-selfservice/artifacts/tfwsm7zn_Logo%2BNuevo%2BMi-Token.png.webp"
              alt="Mi-Token Logo"
              className="h-16 w-auto"
            />
            <p className="text-sm text-slate-300 font-ibm">Self-Service Portal</p>
          </div>
          <h1 className="font-outfit font-bold text-5xl mb-4 tracking-tight">
            Secure Token
            <br />
            Management
          </h1>
          <p className="text-slate-300 text-lg font-ibm leading-relaxed max-w-md">
            Enterprise-grade authentication tokens for your organization. Manage soft-tokens and desktop tokens with ease.
          </p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex flex-col">
        {/* Language Switcher */}
        <div className="absolute top-6 right-6 z-20">
          <LanguageSwitcher />
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md space-y-8">
            {/* Logo for mobile */}
            <div className="lg:hidden flex flex-col items-center mb-8 space-y-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_id-selfservice/artifacts/tfwsm7zn_Logo%2BNuevo%2BMi-Token.png.webp"
                alt="Mi-Token Logo"
                className="h-16 w-auto"
              />
              <p className="text-sm text-muted-foreground font-ibm">Self-Service Portal</p>
            </div>

            {/* Title */}
            <div className="text-center lg:text-left" data-testid="login-header">
              <h2 className="font-outfit font-bold text-4xl sm:text-5xl text-foreground mb-3 tracking-tight">
                {t('login.title')}
              </h2>
              <p className="text-muted-foreground font-ibm text-base">
                {t('login.subtitle')}
              </p>
            </div>

            {/* Instructions */}
            <div className="bg-muted/50 border border-border rounded-lg p-6" data-testid="login-instructions">
              <p className="text-sm text-muted-foreground font-ibm leading-relaxed">
                {fetchingLoginType ? 'Loading...' : getInstructions()}
              </p>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="username" className="font-ibm font-medium">
                  {t('login.username')}
                </Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onBlur={handleUsernameBlur}
                  required
                  className="h-12 font-ibm"
                  data-testid="username-input"
                  placeholder="Enter your username"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="font-ibm font-medium">
                  {getPasswordLabel()}
                  {checkingTokens && <span className="text-xs text-muted-foreground ml-2">(checking...)</span>}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 font-ibm"
                  data-testid="password-input"
                  placeholder={getPasswordPlaceholder()}
                  disabled={checkingTokens}
                />
                {/* Show PIN+OTP note for Token, Adaptive (OTP), and Mixed modes */}
                {(loginType === 'token' || 
                  (loginType === 'adaptive' && adaptiveAuthType === 'otp') || 
                  loginType === 'mixed') && (
                  <p className="text-xs text-amber-600 font-ibm mt-1 flex items-start gap-1">
                    <span className="font-semibold">*</span>
                    <span>If your token has a PIN, type the PIN followed by the OTP (e.g., 1234567890 for PIN:1234 + OTP:567890)</span>
                  </p>
                )}
              </div>

              {loginType === 'mixed' && (
                <div className="space-y-2">
                  <Label htmlFor="otp" className="font-ibm font-medium">
                    One-Time Password (OTP)
                  </Label>
                  <Input
                    id="otp"
                    type="password"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                    className="h-12 font-ibm"
                    data-testid="otp-input"
                    placeholder="Enter your OTP"
                  />
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-12 font-ibm font-medium text-base bg-primary hover:bg-primary/90"
                disabled={loading || fetchingLoginType || checkingTokens}
                data-testid="login-button"
              >
                {loading ? 'Signing in...' : t('login.button')}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
